import {
  ApolloError,
  OperationVariables,
  MutationOptions,
  ApolloClient,
  NormalizedCacheObject,
  MaybeMasked
} from '@apollo/client';
import { ApplicationError, ApplicationErrorCode, ValidationError } from '.';
import { redirect, ActionFunction } from 'react-router-dom';
import { getApplicationError, getValidationErrors } from './errors';

export type ActionResult<T> =
  | {
      data?: T | null;
      serverError?: ApolloError;
      applicationError?: ApplicationError;
      validationErrors?: ValidationError[];
    }
  | undefined;

export const byMethod =
  (actions: {
    [K in 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE']?: ActionFunction;
  }): ActionFunction =>
  async (args) =>
    actions[args.request.method]?.(args);

export const mutate = async <Q, V extends OperationVariables>(
  client: ApolloClient<NormalizedCacheObject>,
  mutationOptions: MutationOptions<Q, V>,
  callback?: (data?: MaybeMasked<Q> | null) => Promise<Response | ActionResult<MaybeMasked<Q>>>
): Promise<Response | ActionResult<MaybeMasked<Q>>> => {
  const { data, errors } = await client.mutate<Q, V>(mutationOptions);

  const applicationError = getApplicationError(new ApolloError({ graphQLErrors: errors }));
  const validationErrors = getValidationErrors(new ApolloError({ graphQLErrors: errors }));

  if (applicationError?.code === ApplicationErrorCode.Unauthorized) {
    return redirect('/signin');
  } else if (applicationError) {
    return { applicationError };
  } else if (validationErrors?.length) {
    return { validationErrors };
  } else if (errors?.length) {
    throw new ApolloError({ graphQLErrors: errors });
  }

  return callback ? callback(data) : { data };
};
